/**
 * Footer -- Version display + "Check for updates" button.
 *
 * Shows at the very bottom of the home page, subtle and non-intrusive.
 * Also accessible from Settings > About.
 */

import type { AweniContext } from '../context/AweniContext';
import { updateManager } from '../services/UpdateManager';

const APP_VERSION = '1.1.0';

export function mountFooter(container: HTMLElement, ctx: AweniContext): void {
  const t = ctx.i18n.t.bind(ctx.i18n);

  container.innerHTML = `
    <div style="text-align:center;padding:24px 16px 80px;opacity:0.5;font-size:12px;color:var(--text-secondary);">
      <span>AWENI v${APP_VERSION}</span>
      <span style="margin:0 6px;">&middot;</span>
      <button id="aweni-check-update-btn" style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:12px;text-decoration:underline;">
        \u{1F504} ${t('footer_check_updates')}
      </button>
      <span style="margin:0 6px;">&middot;</span>
      <span>&copy; 2026 Learnature-ly</span>
      <div id="aweni-update-status" style="margin-top:4px;font-size:11px;"></div>
    </div>
  `;

  const btn = container.querySelector('#aweni-check-update-btn') as HTMLElement;
  const status = container.querySelector('#aweni-update-status') as HTMLElement;

  if (btn) {
    btn.addEventListener('click', async () => {
      btn.textContent = t('common_loading');
      status.textContent = '';

      try {
        const manifest = await updateManager.checkForUpdates();

        if (manifest) {
          status.innerHTML = `<span style="color:#10B981;">${t('footer_update_available').replace('{version}', manifest.app_version)}</span>`;
          btn.textContent = t('footer_tap_to_update');
          btn.onclick = async () => {
            // Clear all caches
            if ('caches' in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k)));
            }
            // Update SW
            const reg = await navigator.serviceWorker?.getRegistration();
            if (reg) await reg.update();
            // Hard refresh
            window.location.reload();
          };
        } else {
          status.innerHTML = `<span style="color:#10B981;">\u2713 ${t('settings_up_to_date')}</span>`;
          btn.textContent = `\u{1F504} ${t('footer_check_updates')}`;
        }
      } catch {
        status.innerHTML = `<span style="color:#EF4444;">${t('common_error')}</span>`;
        btn.textContent = `\u{1F504} ${t('footer_check_updates')}`;
      }
    });
  }
}

/**
 * Utility: perform a hard refresh clearing all caches.
 * Can be called from Settings > About > Check for updates.
 */
export async function performHardRefresh(): Promise<void> {
  // Clear all caches
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  // Update SW
  const reg = await navigator.serviceWorker?.getRegistration();
  if (reg) await reg.update();
  // Hard refresh
  window.location.reload();
}
